import { Box, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { QUERY_KEY } from 'constants/constants';
import { DATE_STANDARD_FORMAT, DATE_VISUALIZE_FORMAT } from 'constants/date';
import ReactECharts from 'echarts-for-react';
import { IFruitGradeData } from 'interfaces/erms';
import moment from 'moment';
import { FC, useState } from 'react';
import { useQuery } from 'react-query';
import { getFruitGradeData } from 'services/erms';
import { useAppSelector } from 'store/hooks';
import { mapViewSelector } from 'store/slices/mapViewSlice';
import ContainerBox from '../../components/ContainerBox';

const colorList = ['green', 'yellow', 'orange', 'blue', 'red'];

interface FruitGradeBoxProps {
  hoverDatePoint: string;
}

const FruitGradeBox: FC<FruitGradeBoxProps> = ({ hoverDatePoint }) => {
  const [selectedFruitGrade, setSelectedFruitGrade] = useState<string>('all');
  const [fruitGradeData, setFruitGradeData] = useState<IFruitGradeData[]>([]);

  const { levelId } = useAppSelector(mapViewSelector);

  const reducedFruitGrade = fruitGradeData?.reduce(
    (acc, cur) => {
      acc.totalHvRipe += cur.totalHvRipe;
      acc.totalHvOverripe += cur.totalHvOverripe;
      acc.totalHvUnderripe += cur.totalHvUnderripe;
      acc.totalHvUnripe += cur.totalHvUnripe;
      acc.totalOthers += cur.totalHvRotten + cur.totalHvLongstalk + cur.totalHvEmpty + cur.totalHvRatdamage;

      return acc;
    },
    {
      totalHvRipe: 0,
      totalHvOverripe: 0,
      totalHvUnderripe: 0,
      totalHvUnripe: 0,
      totalOthers: 0,
    }
  );

  useQuery(
    [QUERY_KEY.GET_FRUIT_GRADE_DATA, hoverDatePoint],
    () => getFruitGradeData({ levelId: levelId!, selectedDate: hoverDatePoint }),
    {
      enabled: !!levelId && !!hoverDatePoint,
      onSuccess(data) {
        setFruitGradeData(data);
      },
    }
  );

  const handleChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setSelectedFruitGrade(value);
  };

  const seriesData = Object.values(reducedFruitGrade).map((value, index) => ({
    value: value,
    itemStyle: {
      color: colorList[index],
    },
  }));

  const option = {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c}',
      backgroundColor: 'rgba(50,50,50,0.7)',
      borderColor: '#333',
      borderWidth: 1,
      textStyle: {
        color: '#fff',
      },
    },
    xAxis: {
      type: 'category',
      data: ['Ripe', 'Over Ripe', 'Under Ripe', 'Unripe', 'Others'],
      axisLabel: {
        rotate: 60,
        align: 'center',
        margin: 40,
      },
    },
    yAxis: {
      type: 'value',
    },
    series: [
      {
        data: seriesData,
        type: 'bar',
      },
    ],
  };

  return (
    <ContainerBox>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography fontSize="16px" fontWeight={600}>
            Fruit Grade
          </Typography>
          <Typography>{moment(hoverDatePoint, DATE_STANDARD_FORMAT).format(DATE_VISUALIZE_FORMAT)}</Typography>
        </Box>
        <Select size="small" value={selectedFruitGrade} onChange={handleChange}>
          <MenuItem value="all">All Grades</MenuItem>
        </Select>
      </Box>

      <ReactECharts option={option} notMerge={true} lazyUpdate={true} theme={'theme_name'} />
    </ContainerBox>
  );
};

export default FruitGradeBox;
